import { useRetryPlanSync } from '../hooks/usePlanMutations';
import { usePlanById } from '../hooks/usePlans';
import { PlanSyncStatus, type PlanDetail } from '../types/plans.types';
import {
  formatPlanPrice,
  getSyncStatusLabel,
  getSyncStatusVariant,
  getVisibilityLabel,
} from '../utils/plan-sync-status';
import { PriceMigrationPanel } from './PriceMigrationPanel';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';
import { formatAdminDateTime } from '@/shared/utils/format-date.utils';

interface PlanDetailModalProps {
  planId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FieldProps {
  label: string;
  value: React.ReactNode;
  testId?: string;
}

function Field({ label, value, testId }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-primary text-xs">{label}</span>
      <span data-testid={testId} className="text-light text-sm">
        {value}
      </span>
    </div>
  );
}

/**
 * Un identificador ausente se declara en vez de dejar el hueco vacio: sin nada escrito no se
 * distingue "todavia no sincroniza" de "el modal no lo pinta", que es justo lo que pasaba.
 */
function StripeIdField({
  label,
  value,
  testId,
}: {
  label: string;
  value: string | null;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-primary text-xs">{label}</span>
      <span
        data-testid={testId}
        className={value ? 'text-light font-mono text-xs break-all' : 'text-primary text-xs italic'}
      >
        {value ?? 'Sin sincronizar'}
      </span>
    </div>
  );
}

const LIMIT_FIELDS: { label: string; key: keyof PlanDetail }[] = [
  { label: 'Usuarios', key: 'maxUsers' },
  { label: 'Regiones', key: 'maxRegions' },
  { label: 'Temas activos', key: 'maxActiveTopics' },
  { label: 'Actores activos', key: 'maxActiveActors' },
  { label: 'Análisis de IA al mes', key: 'maxAiAnalysisMonthly' },
  { label: 'Alertas', key: 'maxAlerts' },
  { label: 'Reportes programados', key: 'maxScheduledReports' },
];

export function PlanDetailModal({ planId, open, onOpenChange }: PlanDetailModalProps) {
  const { plan, isLoading, isError } = usePlanById(open ? planId : '');
  const { retrySync, isRetrying } = useRetryPlanSync(planId);

  const handleRetrySync = () => {
    retrySync(undefined, {
      onSuccess: () => Notification.success({ title: 'Sincronización reintentada' }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plan-detail-modal" className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{plan?.name ?? 'Detalle del plan'}</DialogTitle>
        </DialogHeader>

        {isLoading && <div data-testid="plan-detail-loading" className="py-8" aria-hidden />}

        {!isLoading && isError && (
          <p data-testid="plan-detail-error" className="text-negative text-sm">
            No se pudo cargar el plan. Vuelve a intentarlo.
          </p>
        )}

        {!isLoading && !isError && plan && (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label="Visibilidad"
                value={getVisibilityLabel(plan.visibility)}
                testId="plan-detail-visibility"
              />
              <Field
                label="Estado"
                value={<StatusBadge status={plan.isActive ? 'active' : 'inactive'} />}
              />
              <Field
                label="Precio mensual"
                value={formatPlanPrice(plan.priceMonthly)}
                testId="plan-detail-price-monthly"
              />
              <Field
                label="Precio anual"
                value={formatPlanPrice(plan.priceAnnual)}
                testId="plan-detail-price-annual"
              />
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-primary text-xs">Límites del plan</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {LIMIT_FIELDS.map(({ label, key }) => (
                  <Field
                    key={key}
                    label={label}
                    value={String(plan[key])}
                    testId={`plan-detail-${key}`}
                  />
                ))}
              </div>
              <p className="text-primary text-xs">
                Las cuentas ya suscritas conservan los límites que contrataron. Estos aplican solo a
                contrataciones nuevas.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-primary text-xs">Sincronización con Stripe</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field
                  label="Estado"
                  value={
                    <StatusBadge
                      status={getSyncStatusVariant(plan.syncStatus)}
                      label={getSyncStatusLabel(plan.syncStatus)}
                    />
                  }
                />
                <Field
                  label="Última sincronización"
                  value={formatAdminDateTime(plan.lastSyncedAt)}
                  testId="plan-detail-last-synced"
                />
              </div>

              {/* Los identificadores existen para ir a buscarlos a Stripe, asi que se muestran
                  completos y en monoespaciado en vez de recortados. */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <StripeIdField
                  label="ID de producto"
                  value={plan.stripeProductId}
                  testId="plan-detail-stripe-product-id"
                />
                <StripeIdField
                  label="ID de precio mensual"
                  value={plan.stripeMonthlyPriceId}
                  testId="plan-detail-stripe-monthly-price-id"
                />
                <StripeIdField
                  label="ID de precio anual"
                  value={plan.stripeAnnualPriceId}
                  testId="plan-detail-stripe-annual-price-id"
                />
              </div>

              {!plan.isOfferable && (
                <p data-testid="plan-detail-not-offerable" className="text-warn text-xs">
                  Este plan no se está ofreciendo. Sin precios en Stripe no hay con qué cobrarle a
                  una cuenta.
                </p>
              )}

              {plan.syncError && (
                <p data-testid="plan-detail-sync-error" className="text-negative text-xs">
                  {plan.syncError}
                </p>
              )}

              {/* Solo cuando fallo: reintentar algo que ya esta sincronizado no hace nada
                  y la API lo rechaza con Plan.AlreadySynced. */}
              {plan.syncStatus === PlanSyncStatus.Failed && (
                <Protected permissions={PERMISSIONS.PLANS_MANAGE}>
                  <div>
                    <UnifiedButton
                      variant="outline"
                      onClick={handleRetrySync}
                      disabled={isRetrying}
                      data-testid="retry-sync-btn"
                    >
                      Reintentar sincronización
                    </UnifiedButton>
                  </div>
                </Protected>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-primary text-xs">Migraciones de precio</p>
              <PriceMigrationPanel planId={plan.id} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
